/**
 * WebBeds post-booking disclosures: the rate's TARIFF NOTES and the property fees
 * that are NOT included in the price (paid at the hotel).
 *
 * WebBeds makes both mandatory on the booking confirmation and the voucher. They
 * ride on the hotel trip-summary booking (`/trips/hotels` → `bookingInfo[i]`):
 *
 *   supplier                              "WEBBED"
 *   hotelInformation.tariffNotes          supplier free-text, may be entity-encoded markup
 *   roomInfo.priceInfo.dueAtCheckIn       the non-included fee, in…
 *   roomInfo.priceInfo.currency           …this currency
 *
 * The plain `/trips/summary` booking carries `supplier` and `tariffNotes` but no
 * `roomInfo`, so the fee is only knowable from the hotel trip summary.
 *
 * Gated on the supplier: every other supplier keeps rendering exactly as before.
 */
import { formatAmount } from "../../../helpers/cleanQueryValue";
import { currencySymbol } from "../../currencyFormat";
import { ExtraInfoSection, parseHotelExtraInfo } from "./parseHotelExtraInfo";

export const WEBBED_SUPPLIER = "WEBBED";

export const isWebbedSupplier = (supplier: unknown): boolean =>
  typeof supplier === "string" && supplier.trim().toUpperCase() === WEBBED_SUPPLIER;

export interface WebbedHotelNotes {
  hotelName: string;
  /** Parsed to TEXT (never HTML) — see parseHotelExtraInfo. Empty when none. */
  tariffNotes: ExtraInfoSection[];
  /** Non-included property fee, 0 when none. */
  propertyFee: number;
  /** e.g. "$31.00"; "" when there is no fee. */
  propertyFeeText: string;
}

const toAmount = (value: unknown): number => {
  const n = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * The disclosures for ONE hotel trip-summary booking, or `null` when it is not a
 * WebBeds booking or there is nothing to disclose (no notes and no fee).
 */
export const getWebbedHotelNotes = (booking: any): WebbedHotelNotes | null => {
  if (!booking || !isWebbedSupplier(booking.supplier ?? booking.hotelInformation?.supplier)) return null;
  const info = booking.hotelInformation || {};
  const priceInfo = booking.roomInfo?.priceInfo || {};
  const tariffNotes = parseHotelExtraInfo(info.tariffNotes ?? booking.tariffNotes);
  const propertyFee = toAmount(priceInfo.dueAtCheckIn ?? booking.dueAtCheckIn);
  if (tariffNotes.length === 0 && propertyFee === 0) return null;
  return {
    hotelName: typeof info.name === "string" ? info.name : "",
    tariffNotes,
    propertyFee,
    propertyFeeText: propertyFee > 0 ? `${currencySymbol(priceInfo.currency)}${formatAmount(propertyFee)}` : "",
  };
};

/** Every WebBeds booking in a `bookingInfo[]` that has something to disclose. */
export const collectWebbedHotelNotes = (bookings: any): WebbedHotelNotes[] =>
  Array.isArray(bookings)
    ? bookings.map(getWebbedHotelNotes).filter((notes): notes is WebbedHotelNotes => notes !== null)
    : [];

/** Copy shared by every surface (My Trips, confirmation page, PDF voucher). */
export const WEBBED_NOTES_COPY = {
  propertyFeeLabel: "Property fees not included in the price",
  propertyFeeHint: "Payable directly at the property",
  tariffNotesLabel: "Tariff Notes",
};
